import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import ProjectDocumentService from "../Services/ProjectDocumentService";
import FolderTree from "./FolderTree";

import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";

import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import TableChartIcon from "@mui/icons-material/TableChart";
import Menu from "@mui/material/Menu";

const getFileIcon = (name, folder) => {
  if (folder) return <FolderIcon sx={{ color: "#f59e0b" }} />;

  const ext = name.split(".").pop().toLowerCase();

  if (["xls", "xlsx", "csv"].includes(ext))
    return <TableChartIcon sx={{ color: "#22c55e" }} />;

  if (["pdf"].includes(ext))
    return <PictureAsPdfIcon sx={{ color: "#ef4444" }} />;

  if (["png", "jpg", "jpeg"].includes(ext))
    return <ImageIcon sx={{ color: "#3b82f6" }} />;

  return <InsertDriveFileIcon sx={{ color: "#64748b" }} />;
};

/* ---------------- HELPERS ---------------- */
const formatSize = (bytes) =>
  bytes > 1024 * 1024
    ? (bytes / (1024 * 1024)).toFixed(1) + " MB"
    : (bytes / 1024).toFixed(1) + " KB";

const formatDateTime = (d) =>
  new Date(d).toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

const getFileType = (name, isFolder) => {
  if (isFolder) return "Folder";
  const ext = name.split(".").pop().toLowerCase();
  return ext.toUpperCase() + " File";
};

const ProjectDocuments = ({ expandAll }) => {
  const { organizationId, projectId } = useParams();

  const [currentPath, setCurrentPath] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("files");
  const [sortBy, setSortBy] = useState("name");

  const [selected, setSelected] = useState(null);
  const [clipboard, setClipboard] = useState(null);

  /*  INLINE RENAME STATE */
  const [inlineRenamePath, setInlineRenamePath] = useState(null);
  const [inlineRenameValue, setInlineRenameValue] = useState("");

  const [documentsExpanded, setDocumentsExpanded] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const fileInputRef = useRef(null);

  const [contextMenu, setContextMenu] = useState(null);
  const handleContextMenu = (event, item) => {
    event.preventDefault();
    setSelected(item);

    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  /* ================= LOAD ================= */
  const loadDocuments = async (path = "") => {
    setLoading(true);
    try {
      const res = await ProjectDocumentService.listDocuments(
        organizationId,
        projectId,
        path,
      );
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };

  const viewFile = (item) => {
    const url = ProjectDocumentService.viewUrl(
      organizationId,
      projectId,
      item.relativePath,
    );
    window.open(url, "_blank");
  };

  useEffect(() => {
    if (activeTab === "files") {
      loadDocuments(currentPath);
    }
  }, [currentPath, activeTab]);

  /* ================= SORT ================= */
  const sortedItems = useMemo(() => {
    let filtered = [...items];

    if (searchTerm.trim()) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    return sortBy === "date"
      ? filtered.sort((a, b) => b.lastModified - a.lastModified)
      : filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [items, sortBy, searchTerm]);

  /* ================= FILE OPS ================= */
  const downloadFile = (item) => {
    const url = ProjectDocumentService.downloadUrl(
      organizationId,
      projectId,
      item.relativePath,
    );
    window.open(url, "_blank");
  };

  const deleteItem = async (item) => {
    if (!item) return;
    await ProjectDocumentService.deletePath(
      organizationId,
      projectId,
      item.relativePath,
    );
    setSelected(null);
    loadDocuments(currentPath);
  };

  /* ================= INLINE RENAME ================= */
  const startInlineRename = (item) => {
    setInlineRenamePath(item.relativePath);
    setInlineRenameValue(item.name);
  };

  const commitInlineRename = async (item) => {
    if (!inlineRenameValue.trim()) return;

    await ProjectDocumentService.renamePath(
      organizationId,
      projectId,
      item.relativePath,
      inlineRenameValue,
    );

    setInlineRenamePath(null);
    loadDocuments(currentPath);
  };

  const cancelInlineRename = () => {
    setInlineRenamePath(null);
  };

  /* ================= COPY / CUT / PASTE ================= */
  const copy = () => setClipboard({ type: "copy", item: selected });
  const cut = () => setClipboard({ type: "cut", item: selected });

  const paste = async () => {
    if (!clipboard) return;

    if (clipboard.type === "cut") {
      await ProjectDocumentService.movePath(
        organizationId,
        projectId,
        clipboard.item.relativePath,
        currentPath,
      );
    } else {
      await ProjectDocumentService.copyPath(
        organizationId,
        projectId,
        clipboard.item.relativePath,
        currentPath,
      );
    }

    setClipboard(null);
    loadDocuments(currentPath);
  };

  /* ================= UPLOAD ================= */
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      await ProjectDocumentService.uploadFile(
        organizationId,
        projectId,
        currentPath,
        file,
        () => {},
      );
    }
    e.target.value = "";
    setActiveTab("files");
    loadDocuments(currentPath);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);

    if (!files.length) return;

    for (const file of files) {
      await ProjectDocumentService.uploadFile(
        organizationId,
        projectId,
        currentPath,
        file,
        () => {},
      );
    }

    loadDocuments(currentPath);
    setActiveTab("files");
  };
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <div className="documents-layout" style={{ marginTop: 30 }}>
      <div className="documents-tree">
        <FolderTree
          orgId={organizationId}
          projectId={projectId}
          expandAll={expandAll}
          onSelect={(path) => setCurrentPath(path)}
        />
      </div>

      <div className="documents-content">
        {/* ===== DOCUMENTS HEADER (API-like) ===== */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        ></div>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            📁 Project Documents
          </Typography>

          <TextField
            size="small"
            label="Search documents"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 260 }}
          />
        </Box>

        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: "pointer" }}
            onClick={() => setCurrentPath("")}
          >
            Root
          </Link>

          {currentPath &&
            currentPath.split("/").map((folder, index) => {
              const path = currentPath
                .split("/")
                .slice(0, index + 1)
                .join("/");

              return (
                <Link
                  key={path}
                  underline="hover"
                  color="inherit"
                  sx={{ cursor: "pointer" }}
                  onClick={() => setCurrentPath(path)}
                >
                  {folder}
                </Link>
              );
            })}
        </Breadcrumbs>

        {/* TABS */}
        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          <Button
            variant={activeTab === "upload" ? "contained" : "outlined"}
            onClick={() => setActiveTab("upload")}
            startIcon={<UploadIcon />}
          >
            Upload
          </Button>
          <Button
            variant={activeTab === "drag" ? "contained" : "outlined"}
            onClick={() => setActiveTab("drag")}
          >
            Drag & Drop
          </Button>{" "}
          <Button
            variant={activeTab === "files" ? "contained" : "outlined"}
            onClick={() => setActiveTab("files")}
          >
            Files
          </Button>{" "}
        </Box>

        {/* UPLOAD TAB */}
        {activeTab === "upload" && (
          <>
            <button onClick={() => fileInputRef.current.click()}>
              Browse Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleUpload}
            />
          </>
        )}

        {activeTab === "drag" && (
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{
              border: "2px dashed #94a3b8",
              background: "#f8fafc",
              padding: "60px 20px",
              textAlign: "center",
              borderRadius: 8,
              marginTop: 20,
              cursor: "pointer",
            }}
          >
            <div style={{ fontSize: 40 }}>⬆</div>
            <h3>Drop a file here to upload</h3>
            <p style={{ color: "#64748b" }}>
              or drag & drop files from your computer
            </p>
          </div>
        )}

        {/* FILES TAB */}
        {activeTab === "files" && (
          <>
            {/* ACTION BAR */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 2,
              }}
            >
              {" "}
              <Select
                size="small"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="name">Sort by Name</MenuItem>
                <MenuItem value="date">Sort by Date</MenuItem>
              </Select>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={!selected}
                onClick={copy}
              >
                Copy
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCutIcon />}
                disabled={!selected}
                onClick={cut}
              >
                Cut
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentPasteIcon />}
                disabled={!clipboard}
                onClick={paste}
              >
                Paste
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DriveFileRenameOutlineIcon />}
                disabled={!selected}
                onClick={() => startInlineRename(selected)}
              >
                Rename
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DeleteIcon />}
                disabled={!selected}
                color="error"
                onClick={() => deleteItem(selected)}
              >
                Delete
              </Button>
            </Box>

            {/* HEADER */}
            <Paper
              elevation={2}
              sx={{ mt: 2, borderRadius: 2, overflow: "hidden" }}
            >
              <Table size="small">
                <TableHead sx={{ background: "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Date modified
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedItems.map((item) => (
                    <TableRow
                      key={item.relativePath}
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:hover": { background: "#f5f7fb" },
                        "&.Mui-selected": { background: "#e8f0fe" },
                      }}
                      onContextMenu={(e) => handleContextMenu(e, item)}
                      selected={selected?.relativePath === item.relativePath}
                      onClick={() => setSelected(item)}
                      onDoubleClick={() => {
                        if (item.folder) {
                          setCurrentPath(
                            currentPath
                              ? `${currentPath}/${item.name}`
                              : item.name,
                          );
                        }
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                          }}
                        >
                          {getFileIcon(item.name, item.folder)}

                          {inlineRenamePath === item.relativePath ? (
                            <TextField
                              size="small"
                              autoFocus
                              value={inlineRenameValue}
                              onChange={(e) =>
                                setInlineRenameValue(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitInlineRename(item);
                                if (e.key === "Escape") cancelInlineRename();
                              }}
                            />
                          ) : (
                            item.name
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>{formatDateTime(item.lastModified)}</TableCell>
                      <TableCell>
                        {getFileType(item.name, item.folder)}
                      </TableCell>
                      <TableCell>
                        {item.folder ? "-" : formatSize(item.size)}
                      </TableCell>

                      <TableCell>
                        {!item.folder && (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                viewFile(item);
                              }}
                            >
                              View
                            </Button>

                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadFile(item);
                              }}
                            >
                              Download
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Menu
                open={contextMenu !== null}
                onClose={closeContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                  contextMenu !== null
                    ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                    : undefined
                }
              >
                <MenuItem onClick={copy}>Copy</MenuItem>
                <MenuItem onClick={cut}>Cut</MenuItem>
                <MenuItem onClick={paste}>Paste</MenuItem>
                <MenuItem onClick={() => startInlineRename(selected)}>
                  Rename
                </MenuItem>
                <MenuItem onClick={() => deleteItem(selected)}>Delete</MenuItem>
              </Menu>
            </Paper>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectDocuments;
